'use client';

import { useState, useEffect, useRef } from 'react';
import { getAPI, getAuthAPI } from '~/lib/api';
import {
  getDefaultMessageTitle,
  getMatchingEmailDefaultMessage,
  getPersonalMessageBody,
  toPropertyDetails,
} from '~/lib/messages/speculatorMessageHelpers';
import type { UserDetails } from '~/lib/messages/speculatorMessageHelpers';
import { buildHomeDetailsAndUrl } from '~/lib/speculatorEmailPreview';
import { getCategoryText } from '@habity/services';
import { MAX_MESSAGE_RECIPIENTS } from '~/constants';
import { SITE_URL } from '~/constants/app';
import { API_PATHS } from '@habity/constants';
import { createClient } from '~/lib/providers/supabase/client';

export interface RecentNotification {
  id: number;
  title: string;
  message: string;
  sent_at: string | null;
  is_sent: boolean;
  recipient_count: number;
  created_at: string;
  channels: string[];
  sender: {
    id: number;
    first_name: string | null;
    last_name: string | null;
  } | null;
}

/** Speculators with match_score >= threshold; null = not loaded. When count is 0, maxMatchScoreInSearch is the highest % in the search (for hint). */
export function useEmailRecipients(
  isOpen: boolean,
  queryParams: string,
  minMatchScore: number
) {
  const [emailRecipientIds, setEmailRecipientIds] = useState<number[] | null>(null);
  const [maxMatchScoreInSearch, setMaxMatchScoreInSearch] = useState<number | null>(null);

  useEffect(() => {
    if (!isOpen || !queryParams) {
      setEmailRecipientIds(null);
      setMaxMatchScoreInSearch(null);
      return;
    }
    let cancelled = false;
    setEmailRecipientIds(null);
    setMaxMatchScoreInSearch(null);
    const params = `${queryParams}&minMatchScore=${minMatchScore}&limit=${MAX_MESSAGE_RECIPIENTS}&notificationChannels=email`;
    getAuthAPI(API_PATHS.PROVIDERS.NEONDATABASE.SPECULATORS, params)
      .then((data: unknown) => {
        if (cancelled) return;
        const list = Array.isArray(data) ? data : [];
        setEmailRecipientIds(list.map((b: { id: number }) => b.id));
        if (list.length === 0) {
          getAuthAPI(
            API_PATHS.PROVIDERS.NEONDATABASE.SPECULATORS,
            `${queryParams}&minMatchScore=0&limit=1`
              + `&notificationChannels=email`
          )
            .then((top: unknown) => {
              if (cancelled) return;
              const arr = Array.isArray(top) ? top : [];
              const score = (arr[0] as { match_score?: number } | undefined)?.match_score;
              if (typeof score === 'number' && Number.isFinite(score)) {
                setMaxMatchScoreInSearch(Math.round(score));
              }
            })
            .catch(() => {});
        }
      })
      .catch(() => {
        if (!cancelled) setEmailRecipientIds([]);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, queryParams, minMatchScore]);

  const recipientCount = emailRecipientIds === null ? 0 : emailRecipientIds.length;
  const isLoadingRecipients = !!queryParams && emailRecipientIds === null;

  return { emailRecipientIds, recipientCount, isLoadingRecipients, maxMatchScoreInSearch };
}

export type HomePreview = {
  detailsSentenceForEmail: string | null;
  url: string | null;
  /** Street for matching-email subject line */
  street: string | null;
  /** Full address for subject line (e.g. "Street, City") */
  fullAddress: string | null;
  /** For matching-email default message (intro same as plaintext) */
  category: string;
  rooms: number | null;
  living_space: number | null;
  /** From home.status.show_address; when false, intro uses "i området" */
  show_address?: boolean;
};

/**
 * Fetches home details and applies template-specific defaults when template or homeId changes.
 * Personal: inserts details sentence + URL into defaultMessage. Matching: subject + intro + broker footer.
 */
export function useHomePreview(
  isOpen: boolean,
  homeId: number | undefined,
  emailTemplate: 'personal' | 'matching',
  setMessage: React.Dispatch<React.SetStateAction<string>>,
  setHeader?: React.Dispatch<React.SetStateAction<string>>,
  userDetails?: UserDetails | null,
  defaultTitle?: string,
  defaultMessage?: string
) {
  const [homePreview, setHomePreview] = useState<HomePreview | null>(null);
  const lastAppliedRef = useRef<{
    template: 'personal' | 'matching';
    homeId: number | null;
  } | null>(null);
  const cacheRef = useRef<Record<number, HomePreview>>({});

  useEffect(() => {
    if (!isOpen) {
      lastAppliedRef.current = null;
      cacheRef.current = {};
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !homeId) {
      setHomePreview(null);
      return;
    }
    const cached = cacheRef.current[homeId];
    if (cached) {
      setHomePreview(cached);
      return;
    }
    let cancelled = false;
    setHomePreview(null);
    getAPI(`${API_PATHS.PROVIDERS.SUPABASE.HOMES.INDEX}/${homeId}`)
      .then((response: { data?: unknown; error?: boolean }) => {
        if (cancelled) return;
        if (response?.error || !response?.data) {
          setHomePreview(null);
          return;
        }
        // Home API returns home with address and status (status required: homes.status_id NOT NULL)
        const data = response.data as {
          address?: {
            street?: string | null;
            city?: string | null;
            category?: string | null;
            rooms?: number | null;
            living_space?: number | null;
          };
          status: { show_address?: boolean };
        };
        const addr = data?.address;
        const street = addr?.street ?? null;
        const fullAddress =
          [addr?.street, addr?.city].filter(Boolean).join(', ') || null;
        const category =
          (addr?.category && getCategoryText(addr.category as Parameters<typeof getCategoryText>[0])) ||
          'bostad';
        const rooms = addr?.rooms ?? null;
        const living_space = addr?.living_space ?? null;
        const show_address = data?.status?.show_address;
        const baseUrl = SITE_URL || 'https://erikolsson.se';
        const { detailsSentenceForEmail, url } = buildHomeDetailsAndUrl(
          response.data as Parameters<typeof buildHomeDetailsAndUrl>[0],
          baseUrl
        );
        const payload: HomePreview = {
          detailsSentenceForEmail,
          url,
          street,
          fullAddress,
          category,
          rooms,
          living_space,
          show_address,
        };
        cacheRef.current[homeId] = payload;
        setHomePreview(payload);
      })
      .catch(() => {
        if (!cancelled) setHomePreview(null);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, homeId]);

  // Apply template defaults when (template, homeId) changes so flipping templates resets content
  useEffect(() => {
    if (!isOpen || !setHeader) return;
    const currentKey = homeId ?? null;
    const alreadyApplied =
      lastAppliedRef.current?.template === emailTemplate &&
      lastAppliedRef.current?.homeId === currentKey;
    if (alreadyApplied) return;

    const details =
      homePreview &&
      toPropertyDetails({
        street: homePreview.street ?? undefined,
        category: homePreview.category,
        rooms: homePreview.rooms ?? undefined,
        living_space: homePreview.living_space ?? undefined,
        show_address: homePreview.show_address,
      });
    const titleFromPreview =
      homePreview?.fullAddress
        ? getDefaultMessageTitle(
            homePreview.category,
            homePreview.fullAddress,
            homePreview.show_address
          )
        : null;

    if (emailTemplate === 'personal') {
      if (details && homePreview?.url) {
        setHeader(titleFromPreview ?? defaultTitle ?? '');
        setMessage(
          getPersonalMessageBody(details, userDetails ?? undefined, {
            detailsSentence: homePreview.detailsSentenceForEmail,
            url: homePreview.url,
          })
        );
        lastAppliedRef.current = { template: 'personal', homeId: homeId ?? null };
      } else {
        setHeader(defaultTitle ?? '');
        setMessage((defaultMessage ?? '').trim());
        if (homeId == null)
          lastAppliedRef.current = { template: 'personal', homeId: null };
      }
      return;
    }

    if (emailTemplate === 'matching') {
      if (details && homePreview?.street && homeId != null) {
        setHeader(titleFromPreview ?? defaultTitle ?? '');
        setMessage(
          getMatchingEmailDefaultMessage(details, userDetails ?? undefined)
        );
      } else {
        setHeader(defaultTitle ?? '');
        setMessage((defaultMessage ?? '').trim());
      }
      lastAppliedRef.current = { template: 'matching', homeId: homeId ?? null };
    }
  }, [
    isOpen,
    emailTemplate,
    homeId,
    homePreview,
    defaultTitle,
    defaultMessage,
    userDetails,
    setHeader,
    setMessage,
  ]);

  return homePreview;
}

export function useRecentNotifications(isOpen: boolean) {
  const [recentNotifications, setRecentNotifications] = useState<RecentNotification[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const supabase = createClient();

  const fetchRecentNotifications = async (signal?: AbortSignal) => {
    setIsLoadingHistory(true);
    try {
      const { data, error } = await supabase
        .from('notifications')
        .select(
          `
          id,
          title,
          message,
          sender_id,
          user_ids,
          channels,
          scheduled_at,
          sent_at,
          is_sent,
          recipient_count,
          created_at,
          sender:users!sender_id (
            id,
            first_name,
            last_name,
            email
          )
        `
        )
        .contains('channels', ['email'])
        .order('created_at', { ascending: false })
        .limit(5);

      if (signal?.aborted) return;
      if (error) {
        console.error('Error fetching recent notifications:', error);
      } else {
        const transformedData = (data || []).map((notification) => ({
          ...notification,
          sender:
            Array.isArray(notification.sender) && notification.sender.length > 0
              ? notification.sender[0]
              : null,
        })) as RecentNotification[];
        setRecentNotifications(transformedData);
      }
    } catch (err) {
      if (!signal?.aborted) console.error('Error fetching recent notifications:', err);
    } finally {
      if (!signal?.aborted) setIsLoadingHistory(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    const controller = new AbortController();
    fetchRecentNotifications(controller.signal);
    return () => controller.abort();
  }, [isOpen]);

  return { recentNotifications, isLoadingHistory, fetchRecentNotifications };
}
